import React, { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, Eye, Copy, HelpCircle, Trash2, XCircle, CheckCircle, Clock, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from '../UI/Button';
import { MachhaCharacter } from '../../lib/machhaAsset';
import { albumsApi } from '../../api/albums';
import { PhotoReport, ReportReason, ReportStatus } from '../../types';
import styles from './ReportsPanel.module.css';

interface ReportsPanelProps {
  albumId: string;
}

const REASON_ICONS: Record<ReportReason, React.ReactNode> = {
  inappropriate: <AlertTriangle className={styles['icon-md']} />,
  privacy: <Eye className={styles['icon-md']} />,
  duplicate: <Copy className={styles['icon-md']} />,
  other: <HelpCircle className={styles['icon-md']} />,
};

const REASON_LABELS: Record<ReportReason, string> = {
  inappropriate: 'Inappropriate',
  privacy: 'Privacy',
  duplicate: 'Duplicate',
  other: 'Other',
};

const STATUS_CONFIG: Record<ReportStatus, { label: string; styleKey: string; icon: React.ReactNode }> = {
  pending: {
    label: 'Pending',
    styleKey: 'status-pending',
    icon: <Clock className={styles['icon-sm']} />,
  },
  dismissed: {
    label: 'Dismissed',
    styleKey: 'status-dismissed',
    icon: <XCircle className={styles['icon-sm']} />,
  },
  actioned: {
    label: 'Removed',
    styleKey: 'status-actioned',
    icon: <CheckCircle className={styles['icon-sm']} />,
  },
};

type FilterTab = 'pending' | 'all';

export function ReportsPanel({ albumId }: ReportsPanelProps) {
  const [reports, setReports] = useState<PhotoReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterTab, setFilterTab] = useState<FilterTab>('pending');
  const [resolvingId, setResolvingId] = useState<string | null>(null);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const data = await albumsApi.listReports(albumId, filterTab === 'pending' ? 'pending' : undefined);
      setReports(data.reports);
    } catch {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, [albumId, filterTab]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const handleResolve = async (reportId: string, action: 'dismiss' | 'action') => {
    setResolvingId(reportId);
    try {
      await albumsApi.resolveReport(albumId, reportId, { action });
      toast.success(action === 'action' ? 'Photo removed from album' : 'Report dismissed');
      fetchReports();
    } catch {
      toast.error('Failed to resolve report');
    } finally {
      setResolvingId(null);
    }
  };

  const pendingCount = reports.filter((r) => r.status === 'pending').length;

  return (
    <div className={styles['container']}>
      {/* Filter tabs */}
      <div className={styles['filter-tabs']}>
        <button
          onClick={() => setFilterTab('pending')}
          className={[styles['filter-tab'], filterTab === 'pending' ? styles['filter-tab-active'] : styles['filter-tab-inactive']].join(' ')}
        >
          Pending{pendingCount > 0 && filterTab === 'all' ? ` (${pendingCount})` : ''}
        </button>
        <button
          onClick={() => setFilterTab('all')}
          className={[styles['filter-tab'], filterTab === 'all' ? styles['filter-tab-active'] : styles['filter-tab-inactive']].join(' ')}
        >
          All Reports
        </button>
      </div>

      {/* Loading state */}
      {loading && (
        <div className={styles['loading-wrapper']}>
          <Loader2 className={`${styles['loading-spinner']} ${styles['icon-lg']}`} />
        </div>
      )}

      {/* Empty state */}
      {!loading && reports.length === 0 && (
        <div className={styles['empty-state']}>
          <MachhaCharacter state="Empty" part="face" size={80} />
          <p className={styles['empty-text']}>
            {filterTab === 'pending'
              ? 'No pending reports. Your album is looking great!'
              : 'No reports have been submitted yet.'}
          </p>
        </div>
      )}

      {/* Report cards */}
      {!loading && reports.length > 0 && (
        <motion.div
          className={styles['report-list']}
          initial="hidden"
          animate="visible"
          variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.04 } } }}
        >
          <AnimatePresence mode="popLayout">
            {reports.map((report) => {
              const reporter = typeof report.reportedBy === 'object' ? report.reportedBy : null;
              const status = STATUS_CONFIG[report.status];
              const isPending = report.status === 'pending';
              const isResolving = resolvingId === report._id;

              return (
                <motion.div
                  key={report._id}
                  layout
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 28 }}
                  className={styles['report-card']}
                >
                  {/* Top row: photo thumbnail + report info */}
                  <div className={styles['report-top-row']}>
                    {/* Thumbnail */}
                    {report.thumbnailUrl ? (
                      <img
                        src={report.thumbnailUrl}
                        alt={report.originalFilename || 'Reported photo'}
                        className={styles['thumbnail']}
                      />
                    ) : (
                      <div className={styles['thumbnail-placeholder']}>
                        <AlertTriangle className={`${styles['thumbnail-placeholder-icon']} ${styles['icon-lg']}`} />
                      </div>
                    )}

                    {/* Info */}
                    <div className={styles['report-info']}>
                      <div className={styles['badges-row']}>
                        <div className={styles['badges-group']}>
                          <span className={[styles['status-badge'], styles[status.styleKey]].join(' ')}>
                            {status.icon}
                            {status.label}
                          </span>
                          <span className={styles['reason-badge']}>
                            {REASON_ICONS[report.reason]}
                            {REASON_LABELS[report.reason]}
                          </span>
                        </div>
                      </div>

                      {/* Reporter */}
                      <div className={styles['reporter-row']}>
                        {reporter?.picture && (
                          <img
                            src={reporter.picture}
                            alt=""
                            className={styles['reporter-avatar']}
                          />
                        )}
                        <span className={styles['reporter-name']}>
                          {reporter?.name || 'Unknown user'}
                        </span>
                        <span className={styles['reporter-date']}>
                          {new Date(report.createdAt).toLocaleDateString(undefined, {
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                          })}
                        </span>
                      </div>

                      {/* Message */}
                      {report.message && (
                        <p className={styles['report-message']}>
                          "{report.message}"
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Actions — only for pending reports */}
                  {isPending && (
                    <div className={styles['actions-row']}>
                      <Button
                        variant="ghost"
                        size="sm"
                        loading={isResolving}
                        onClick={() => handleResolve(report._id, 'dismiss')}
                      >
                        <XCircle className={styles['icon-btn-sm']} />
                        Dismiss
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        loading={isResolving}
                        onClick={() => handleResolve(report._id, 'action')}
                      >
                        <Trash2 className={styles['icon-btn-sm']} />
                        Remove Photo
                      </Button>
                    </div>
                  )}

                  {/* Resolved info */}
                  {!isPending && report.resolvedAt && (
                    <p className={styles['resolved-info']}>
                      Resolved {new Date(report.resolvedAt).toLocaleDateString(undefined, {
                        month: 'short',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                      })}
                      {report.resolvedBy && typeof report.resolvedBy === 'object'
                        ? ` by ${report.resolvedBy.name}`
                        : ''}
                    </p>
                  )}
                </motion.div>
              );
            })}
          </AnimatePresence>
        </motion.div>
      )}
    </div>
  );
}
